import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import { CREW_MANAGED_SERVICE_CONTRACT } from './sourcing';
import type {
  Application,
  BenefitCategory,
  BenefitRecord,
  Contract,
  CostActual,
  CostAllocation,
  CostCategory,
  FundingLine,
  Product,
  ProductApplicationLink,
  StatementOfWork,
  Team,
  TeamProductLink,
} from './types';
import { at, groupBy, millions, pad, round, splitCents } from './support';

/**
 * Funding, cost actuals, allocations and benefits.
 *
 * Two design choices carry most of the weight here.
 *
 * First, cost sits on cost objects — applications, teams, contracts — and
 * reaches a product only through an allocation. That is how a finance system
 * actually works, and it is the only way the platform can answer "how much of
 * this is attributed and how much is not" instead of asserting a total.
 *
 * Second, a deliberate share of that cost is left unallocated: applications
 * whose split has never been agreed, teams that work across too many products
 * to apportion, and third-party contracts that name no product at all. The
 * temptation is to spread the residue so every total looks complete. The whole
 * proposition of this platform is that it refuses to.
 */

/** Fiscal year to date: 1 January to the dataset's asOf date. */
export const YTD_FRACTION = 140 / 365;

const INTERNAL_LOADED_RATE_CENTS = millions(0.195);
const CONTRACTOR_LOADED_RATE_CENTS = millions(0.285);

/**
 * Pinned year-to-date cost for the north-star product, in millions, split the
 * way the Product 360 reference splits it. Pinned rather than emergent because
 * this is the number the demo is narrated around; the balancing amount is
 * posted to the crew managed-service contract, which is where it would sit.
 */
const PINNED_PRODUCT_COST: Record<string, Record<CostCategory, number>> = {
  'PRD-CDR': { Run: 26.1, Enhance: 11.0, Transform: 4.6, Other: 1.9 },
};

interface BenefitSeed {
  category: BenefitCategory;
  driver: string;
  realizedMillions: number;
  vsTarget: number;
}

const CDR_BENEFITS: readonly BenefitSeed[] = [
  {
    category: 'OperationalEfficiency',
    driver: 'Schedule Optimisation',
    realizedMillions: 22.7,
    vsTarget: 0.12,
  },
  {
    category: 'OperationalEfficiency',
    driver: 'Reserve Utilisation Improvement',
    realizedMillions: 15.9,
    vsTarget: 0.08,
  },
  {
    category: 'OperationalEfficiency',
    driver: 'Roster Publication Automation',
    realizedMillions: 9.6,
    vsTarget: 0.04,
  },
  {
    category: 'CostAvoidance',
    driver: 'Reduced Crew Disruption',
    realizedMillions: 26.4,
    vsTarget: 0.16,
  },
  {
    category: 'CostAvoidance',
    driver: 'Training Cost Reduction',
    realizedMillions: 5.7,
    vsTarget: 0.06,
  },
  {
    category: 'RevenueImpact',
    driver: 'Recovered Trip Revenue',
    realizedMillions: 14.1,
    vsTarget: 0.09,
  },
  {
    category: 'RevenueImpact',
    driver: 'Ancillary Retention',
    realizedMillions: 7.2,
    vsTarget: -0.03,
  },
  {
    category: 'RiskMitigation',
    driver: 'Regulatory Breach Avoidance',
    realizedMillions: 7.4,
    vsTarget: 0.11,
  },
  {
    category: 'RiskMitigation',
    driver: 'Crew Agreement Penalty Avoidance',
    realizedMillions: 4.8,
    vsTarget: 0.02,
  },
];

const GENERIC_DRIVERS: Record<BenefitCategory, readonly string[]> = {
  OperationalEfficiency: ['Process Automation', 'Manual Effort Reduction', 'Cycle Time Reduction'],
  CostAvoidance: ['Licence Rationalisation', 'Incident Cost Avoidance', 'Rework Reduction'],
  RevenueImpact: ['Conversion Uplift', 'Ancillary Attach Rate', 'Retention Improvement'],
  RiskMitigation: [
    'Compliance Exposure Reduction',
    'Resilience Improvement',
    'Audit Finding Closure',
  ],
};

export interface FinanceInputs {
  asOf: string;
  fiscalYear: number;
  rng: Rng;
  products: readonly Product[];
  applications: readonly Application[];
  teams: readonly Team[];
  contracts: readonly Contract[];
  statementsOfWork: readonly StatementOfWork[];
  productApplicationLinks: readonly ProductApplicationLink[];
  teamProductLinks: readonly TeamProductLink[];
}

export interface FinanceData {
  fundingLines: FundingLine[];
  costActuals: CostActual[];
  costAllocations: CostAllocation[];
  benefits: BenefitRecord[];
}

export function buildFinance(inputs: FinanceInputs): FinanceData {
  const { asOf, fiscalYear, products, applications, teams, contracts, statementsOfWork } = inputs;
  const rng = inputs.rng.fork('finance');
  const stamp = provenanceFactory(asOf, inputs.rng.fork('finance-provenance'));

  const costActuals: CostActual[] = [];
  const costAllocations: CostAllocation[] = [];
  let costSeq = 0;
  let allocationSeq = 0;

  const pushCost = (
    kind: CostActual['costObjectKind'],
    objectCode: string,
    category: CostCategory,
    amountCents: number,
  ): CostActual => {
    costSeq += 1;
    const code = `COST-${pad(costSeq, 6)}`;
    const record: CostActual = {
      id: idFor('CostActual', code),
      businessCode: code,
      costObjectKind: kind,
      costObjectCode: objectCode,
      category,
      fiscalYear,
      periodStart: `${fiscalYear}-01-01`,
      periodEnd: asOf,
      amountCents,
      provenance: stamp(SOURCE_SYSTEMS.financeErp, `ERP-${pad(costSeq, 7)}`, {
        meanAgeDays: 11,
        meanConfidence: 0.9,
        dataClassification: 'Confidential',
      }),
    };
    costActuals.push(record);
    return record;
  };

  const pushAllocation = (
    costActualCode: string,
    productCode: string,
    share: number,
    basis: CostAllocation['basis'],
    status: CostAllocation['status'] = 'Allocated',
  ): void => {
    if (share <= 0) return;
    allocationSeq += 1;
    const code = `ALLOC-${pad(allocationSeq, 6)}`;
    costAllocations.push({
      id: idFor('CostAllocation', code),
      businessCode: code,
      costActualId: idFor('CostActual', costActualCode),
      costActualCode,
      productId: idFor('Product', productCode),
      productCode,
      share: round(share, 4),
      percentage: round(share, 4),
      basis,
      status,
      provenance: stamp(SOURCE_SYSTEMS.financeErp, `ERP-ALLOC-${pad(allocationSeq, 7)}`, {
        meanAgeDays: 13,
        meanConfidence: status === 'Disputed' ? 0.42 : 0.87,
        dataClassification: 'Confidential',
      }),
    });
  };

  // ── Applications → Run cost ────────────────────────────────────────────────
  const pinnedProducts = new Set(Object.keys(PINNED_PRODUCT_COST));
  const linksByApp = groupBy(inputs.productApplicationLinks, (l) => l.applicationCode);
  for (const app of applications) {
    if (app.lifecycle === 'Retired' || app.lifecycle === 'Candidate') continue;
    const ytd = Math.round(app.annualRunCostCents * YTD_FRACTION);
    if (ytd <= 0) continue;
    const actual = pushCost('Application', app.businessCode, 'Run', ytd);

    const links = linksByApp.get(app.businessCode) ?? [];
    // An application that a pinned product depends on has its split fully
    // agreed, so the pinned product's headline cost is not disturbed by an
    // unrelated residual. Everywhere else the gaps are left where they fall.
    if (links.some((link) => pinnedProducts.has(link.productCode))) {
      const weights = links.map((link) =>
        link.role === 'Primary' ? 6 : link.role === 'Supporting' ? 3 : 1,
      );
      const weightTotal = weights.reduce((sum, w) => sum + w, 0);
      links.forEach((link, i) => {
        pushAllocation(
          actual.businessCode,
          link.productCode,
          (weights[i] ?? 1) / weightTotal,
          link.role === 'Primary' ? 'Direct' : 'Usage',
        );
      });
      continue;
    }

    for (const link of links) {
      if (link.allocationPercent === null) continue; // split never agreed
      if (link.status !== 'Approved') continue; // proposed edges do not move money
      pushAllocation(
        actual.businessCode,
        link.productCode,
        link.allocationPercent,
        link.role === 'Primary' ? 'Direct' : 'Usage',
      );
    }
  }

  // ── Teams → Enhance and Transform cost ─────────────────────────────────────
  const teamLinksByTeam = groupBy(inputs.teamProductLinks, (l) => l.teamCode);
  teams.forEach((team, index) => {
    const annual =
      team.internalFte * INTERNAL_LOADED_RATE_CENTS +
      team.contractorFte * CONTRACTOR_LOADED_RATE_CENTS;
    const ytd = Math.round(annual * YTD_FRACTION);
    const [enhance, transform] = splitCents(ytd, [65, 35]);
    const enhanceActual = pushCost('Team', team.businessCode, 'Enhance', enhance ?? 0);
    const transformActual = pushCost('Team', team.businessCode, 'Transform', transform ?? 0);

    // Roughly one team in ten spans too many products for anyone to have agreed
    // a split. Its cost stays visible and unattributed.
    if (index % 10 === 7) return;

    const links = teamLinksByTeam.get(team.businessCode) ?? [];
    for (const link of links) {
      const status: CostAllocation['status'] = index % 23 === 5 ? 'Disputed' : 'Allocated';
      pushAllocation(
        enhanceActual.businessCode,
        link.productCode,
        link.capacityShare,
        'Headcount',
        status,
      );
      pushAllocation(
        transformActual.businessCode,
        link.productCode,
        link.capacityShare,
        'Headcount',
        status,
      );
    }
  });

  // ── Contracts → Other cost ─────────────────────────────────────────────────
  const sowsByContract = groupBy(statementsOfWork, (s) => s.contractCode);
  for (const contract of contracts) {
    if (contract.businessCode === CREW_MANAGED_SERVICE_CONTRACT) continue; // handled below
    const ytd = Math.round(contract.annualValueCents * YTD_FRACTION);
    if (ytd <= 0) continue;
    const actual = pushCost('Contract', contract.businessCode, 'Other', ytd);

    const sows = (sowsByContract.get(contract.businessCode) ?? []).filter(
      (sow) =>
        sow.productCode !== null &&
        sow.status !== 'Draft' &&
        // Pinned products take their third-party cost from their own named
        // contract, so a randomly assigned SOW cannot inflate them.
        !pinnedProducts.has(sow.productCode),
    );
    if (sows.length === 0) continue; // no SOW names a product → unallocated
    const totalSowValue = sows.reduce((sum, sow) => sum + sow.valueCents, 0);
    if (totalSowValue <= 0) continue;
    // Only the share covered by product-bearing SOWs is attributed; the rest of
    // the contract stays unallocated rather than being spread across them.
    const coverage = Math.min(1, totalSowValue / contract.annualValueCents);
    for (const sow of sows) {
      if (sow.productCode === null) continue;
      pushAllocation(
        actual.businessCode,
        sow.productCode,
        (sow.valueCents / totalSowValue) * coverage,
        'Direct',
      );
    }
  }

  // ── Pinned product totals ──────────────────────────────────────────────────
  const allocatedByProductCategory = new Map<string, number>();
  const actualByCode = new Map(costActuals.map((c) => [c.businessCode, c]));
  for (const allocation of costAllocations) {
    const actual = actualByCode.get(allocation.costActualCode);
    if (!actual) continue;
    const key = `${allocation.productCode}|${actual.category}`;
    allocatedByProductCategory.set(
      key,
      (allocatedByProductCategory.get(key) ?? 0) +
        Math.round(actual.amountCents * allocation.share),
    );
  }

  for (const [productCode, targets] of Object.entries(PINNED_PRODUCT_COST)) {
    for (const [category, targetMillions] of Object.entries(targets) as [CostCategory, number][]) {
      const already = allocatedByProductCategory.get(`${productCode}|${category}`) ?? 0;
      const delta = millions(targetMillions) - already;
      if (delta <= 0) continue;
      const actual = pushCost('Contract', CREW_MANAGED_SERVICE_CONTRACT, category, delta);
      pushAllocation(actual.businessCode, productCode, 1, 'Direct');
    }
  }

  // ── Funding lines ──────────────────────────────────────────────────────────
  // Planned is a full-year figure; the pages compare year-to-date actuals against
  // the year-to-date share of it, which is why a product can show 76% of plan
  // rather than a meaningless 26%.
  const fundingLines: FundingLine[] = [];
  const activeProducts = products.filter(
    (p) => p.lifecycle === 'Active' || p.lifecycle === 'Sunset',
  );
  activeProducts.forEach((product, index) => {
    const ytdActual = (['Run', 'Enhance', 'Transform', 'Other'] as CostCategory[]).reduce(
      (sum, category) =>
        sum + (allocatedByProductCategory.get(`${product.businessCode}|${category}`) ?? 0),
      0,
    );
    const pinned = PINNED_PRODUCT_COST[product.businessCode];
    const effectiveYtd = pinned
      ? millions(Object.values(pinned).reduce((sum, v) => sum + v, 0))
      : ytdActual;
    // Target a plan the actuals sit at 62–104% of, so the portfolio contains
    // both under- and over-spend rather than a uniformly virtuous story.
    const consumption = product.businessCode === 'PRD-CDR' ? 0.76 : rng.float(0.62, 1.04);
    const planned = Math.max(
      millions(0.2),
      Math.round(effectiveYtd / (consumption * YTD_FRACTION)),
    );
    const code = `FUND-${pad(index + 1, 4)}`;
    fundingLines.push({
      id: idFor('FundingLine', code),
      businessCode: code,
      productCode: product.businessCode,
      fiscalYear,
      plannedCents: planned,
      approvedCents: Math.round(planned * rng.float(0.9, 1.0)),
      provenance: stamp(SOURCE_SYSTEMS.financeErp, `ERP-FUND-${pad(index + 1, 5)}`, {
        meanAgeDays: 24,
        meanConfidence: 0.88,
        dataClassification: 'Confidential',
      }),
    });
  });

  // ── Benefits ───────────────────────────────────────────────────────────────
  const benefits: BenefitRecord[] = [];
  let benefitSeq = 0;
  const pushBenefit = (
    productCode: string,
    category: BenefitCategory,
    driver: string,
    realizedCents: number,
    vsTarget: number,
  ): void => {
    benefitSeq += 1;
    const code = `BEN-${pad(benefitSeq, 5)}`;
    benefits.push({
      id: idFor('Benefit', code),
      businessCode: code,
      productCode,
      category,
      driver,
      targetCents: Math.round(realizedCents / (1 + vsTarget)),
      realizedCents,
      fiscalYear,
      provenance: stamp(SOURCE_SYSTEMS.financeErp, `ERP-BEN-${pad(benefitSeq, 6)}`, {
        meanAgeDays: 17,
        meanConfidence: 0.79,
        dataClassification: 'Confidential',
      }),
    });
  };

  for (const seed of CDR_BENEFITS) {
    pushBenefit(
      'PRD-CDR',
      seed.category,
      seed.driver,
      millions(seed.realizedMillions),
      seed.vsTarget,
    );
  }

  for (const product of activeProducts) {
    if (product.businessCode === 'PRD-CDR') continue;
    // Benefit is scaled off business value, so a product cannot report a return
    // wildly out of proportion to the value it is credited with.
    const pool = Math.round(product.businessValueCents * rng.float(0.18, 0.52));
    const categories: BenefitCategory[] = [
      'OperationalEfficiency',
      'CostAvoidance',
      'RevenueImpact',
      'RiskMitigation',
    ];
    const shares = splitCents(pool, [
      rng.float(2, 5),
      rng.float(1.5, 4),
      rng.float(0.5, 3.5),
      rng.float(0.5, 2),
    ]);
    categories.forEach((category, index) => {
      const amount = at(shares, index);
      if (amount <= 0) return;
      const drivers = GENERIC_DRIVERS[category];
      pushBenefit(
        product.businessCode,
        category,
        at(drivers, (benefitSeq + index) % drivers.length),
        amount,
        round(rng.float(-0.22, 0.24), 3),
      );
    });
  }

  return { fundingLines, costActuals, costAllocations, benefits };
}
